import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

/**
 * Handles per-segment video generation controls on the story short preview step.
 * - "Convert to Video" button -> POST to generate_segment_video
 * - "Revert to Image" button -> POST to revert_segment_video
 * - Listens for ActionCable updates via ShortsWizardChannel to auto-reload on completion
 */
export default class extends Controller {
  static values = {
    configId: Number,
    segmentIndex: Number,
    generateUrl: String,
    revertUrl: String
  }

  connect() {
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  subscribeToChannel() {
    const consumer = createConsumer()
    const controller = this

    this.subscription = consumer.subscriptions.create(
      { channel: "ShortsWizardChannel", config_id: this.configIdValue },
      {
        received(data) {
          if (data.type === "segment_video" &&
              data.segment_index === controller.segmentIndexValue &&
              (data.status === "completed" || data.status === "failed")) {
            window.location.reload()
          }
        }
      }
    )
  }

  async convert(event) {
    const button = event.currentTarget
    const isRedo = button.textContent.trim().startsWith("Redo")
    const message = isRedo
      ? "Regenerate this video? The current video will be replaced and additional credits will be used."
      : "Convert this image to an AI video clip? This will use credits."
    if (!confirm(message)) return

    button.disabled = true
    button.textContent = "Starting..."

    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ segment_index: this.segmentIndexValue })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Reload to show generating state
        window.location.reload()
      } else {
        alert(data.error || "Failed to start video generation")
        button.disabled = false
        button.textContent = "Convert to Video"
      }
    } catch {
      alert("Network error. Please try again.")
      button.disabled = false
      button.textContent = "Convert to Video"
    }
  }

  async revert(event) {
    const button = event.currentTarget
    if (!confirm("Remove the video and revert to the original image?")) return

    button.disabled = true
    button.textContent = "Reverting..."

    try {
      const response = await fetch(this.revertUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ segment_index: this.segmentIndexValue })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        window.location.reload()
      } else {
        alert(data.error || "Failed to revert")
        button.disabled = false
        button.textContent = "Revert to Image"
      }
    } catch {
      alert("Network error. Please try again.")
      button.disabled = false
      button.textContent = "Revert to Image"
    }
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
